import { Draggable, Droppable } from "@hello-pangea/dnd";
import {
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Fade,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from "react";
import AutoScrollContainer from '../AutoScrollContainer';
import KanbanCardItem from '../KanbanCardItem';

// Função de debounce para evitar múltiplas chamadas
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

const KanbanItemList = React.memo(function ItemList({ columnId, column, kanbanId, taskTitle, onLoadMore, loadingCard, priorities, tags, assigneds, expanded, displayFilters }) {
    const containerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const loadingRef = useRef(false);

    // Aumentar o threshold para 1200px para carregar bem antes do fim da lista
    const SCROLL_THRESHOLD = 1200;

    // Implementar o carregamento de mais itens com debounce
    const loadMoreItems = useCallback(() => {
        if (loadingRef.current || !column?.pagination?.hasMore) return;

        loadingRef.current = true;
        setIsLoading(true);

        const loadMorePromise = onLoadMore?.(columnId);

        if (loadMorePromise instanceof Promise) {
            loadMorePromise
                .catch(error => {
                    console.error("Erro ao carregar mais itens:", error);
                })
                .finally(() => {
                    loadingRef.current = false;
                    setIsLoading(false);
                });
        } else {
            setTimeout(() => {
                loadingRef.current = false;
                setIsLoading(false);
            }, 500);
        }
    }, [columnId, column?.pagination?.hasMore, onLoadMore]);

    // Adicionar debounce ao scroll handler para melhorar performance
    const debouncedLoadMore = useCallback(
        debounce(loadMoreItems, 150),
        [loadMoreItems]
    );

    const handleScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // Calcula a distância do fim
        const scrollBottom = scrollHeight - scrollTop - clientHeight;

        // Carrega bem antes do usuário chegar ao fim
        if (scrollBottom < SCROLL_THRESHOLD) {
            debouncedLoadMore();
        }
    }, [debouncedLoadMore]);

    // Garantir que o listener de scroll é removido quando o componente é desmontado
    useEffect(() => {
        return () => {
            loadingRef.current = false;
        };
    }, []);


    return (
        <Droppable droppableId={columnId}>
            {(provided, snapshot) => (
                <AutoScrollContainer
                    ref={(el) => {
                        provided.innerRef(el);
                        containerRef.current = el;
                    }}
                    {...provided.droppableProps}
                    onScroll={handleScroll}
                    style={{
                        height: `calc(100vh - ${expanded ? 295 : 250}px)`,
                        width: '100%',
                        backgroundColor: theme => theme.palette.mode === 'light'
                            ? 'rgba(243, 243, 243, 0.03)'
                            : 'rgba(255,255,255,0.03)',
                        borderRadius: 4,
                        padding: '15px 0px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        position: 'relative' // Para posicionamento de elementos filhos
                    }}
                >
                    {(column?.cardIds || [])?.map((cardId, index) => (
                        <Draggable key={cardId} draggableId={cardId} index={index}>
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                        ...provided.draggableProps.style,
                                        marginBottom: 8
                                    }}
                                >
                                    <Box sx={{ px: 0.75 }}>
                                        <KanbanCardItem
                                            columnId={columnId}
                                            item={column?.cards[cardId]}
                                            isDragging={snapshot.isDragging}
                                            isClone={false}
                                            loadingCard={loadingCard}
                                            priorities={priorities}
                                            tags={tags}
                                            assigneds={assigneds}
                                            displayFilters={displayFilters}
                                            taskTitle={taskTitle}
                                            kanbanId={kanbanId}
                                        />
                                    </Box>
                                </div>
                            )}
                        </Draggable>
                    ))}
                    {provided.placeholder}

                    {/* Indicador de carregamento melhorado com animação */}
                    <Fade in={isLoading} timeout={300}>
                        <Box
                            sx={{
                                display: isLoading ? 'flex' : 'none',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                my: 2,
                                py: 1.5,
                                backgroundColor: theme =>
                                    theme.palette.mode === 'light'
                                        ? 'rgba(0, 0, 0, 0.04)'
                                        : 'rgba(255, 255, 255, 0.05)',
                                borderRadius: 1.5,
                                mx: 1.5
                            }}
                        >
                            <CircularProgress
                                size={24}
                                thickness={4}
                                sx={{
                                    color: theme => theme.palette.primary.main,
                                    mb: 0.5
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.7rem', fontWeight: 500 }}
                            >
                                Carregando...
                            </Typography>
                        </Box>
                    </Fade>

                    {/* Botão "Carregar mais" com estilo melhorado */}
                    {column?.pagination?.hasMore && !isLoading && (
                        <Fade in={!isLoading} timeout={300}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 1.5,
                                    mt: 1,
                                    mx: 1.5,
                                    borderTop: '1px dashed rgba(255,255,255,0.1)',
                                    borderRadius: 1,
                                    backgroundColor: theme =>
                                        theme.palette.mode === 'light'
                                            ? 'rgba(0, 0, 0, 0.02)'
                                            : 'rgba(255, 255, 255, 0.02)',
                                    '&:hover': {
                                        backgroundColor: theme =>
                                            theme.palette.mode === 'light'
                                                ? 'rgba(0, 0, 0, 0.04)'
                                                : 'rgba(255, 255, 255, 0.04)',
                                    }
                                }}
                            >
                                <Button
                                    variant="text"
                                    size="small"
                                    endIcon={<ExpandMoreIcon />}
                                    onClick={loadMoreItems}
                                    disabled={loadingRef.current}
                                    sx={{
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        py: 0.5,
                                        px: 2,
                                        borderRadius: 2
                                    }}
                                >
                                    Carregar mais
                                </Button>
                            </Box>
                        </Fade>
                    )}
                </AutoScrollContainer>
            )}
        </Droppable>
    );
});

export default KanbanItemList;